//ApplicationList.tsx
import React, { useState, useEffect, useMemo } from "react";
import { ApplicationAuditType } from "../data/ApplicationAuditType";
import { fetchApplicationAuditList } from "../../../service/ApplicationAuditService";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Pagination,
  useMediaQuery,
  Chip,
  InputAdornment,
} from "@mui/material";
import {
  ExpandMore as ChevronDown,
  ExpandLess as ChevronUp,
  Photo as PhotoIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageExt from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import { Table as TiptapTable } from '@tiptap/extension-table';
import TiptapTableRow from '@tiptap/extension-table-row';
import TableHeaderExt from '@tiptap/extension-table-header';
import TiptapTableCell from '@tiptap/extension-table-cell';
import Highlight from '@tiptap/extension-highlight';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Link from '@tiptap/extension-link';
import { FontSize, Indent } from '../../form/RichTextToolbar';
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ClearSharpIcon from "@mui/icons-material/ClearSharp";
import dayjs from "dayjs";

// テーブルのスタイルを設定
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.custom.gray,
    color: theme.palette.common.white,
    whiteSpace: "nowrap", // 改行しない
    padding: "10px 20px",
  },
  [`&.${tableCellClasses.body}`]: {
    whiteSpace: "nowrap",
    padding: "6px 20px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const columnLabels: Partial<Record<keyof ApplicationAuditType, string>> = {
  number: "番号",
  submittedAt: "申請日",
  type: "申請種別",
  applicationName: "申請書名",
  applicant: "申請者",
  userProfiles: "申請者情報",
  status: "申請ステータス",
  currentActivity: "現在のアクティビティ",
  formSummary: "申請内容",
  reportFileStatus: "添付ファイル",
};

// 画像ノードをPhotoIconで表示するカスタムNodeView
const ImageAsIcon = () => (
  <NodeViewWrapper as="span" style={{ display: 'inline-flex', verticalAlign: 'middle' }}>
    <PhotoIcon sx={{ fontSize: 16, color: '#1976d2', mx: 0.5 }} />
  </NodeViewWrapper>
);

// 画像をアイコンに置き換えた上でTiptapで読み取り専用表示するコンポーネント
const ImageIconExtension = ImageExt.extend({
  addNodeView() {
    return ReactNodeViewRenderer(ImageAsIcon);
  },
});

function TiptapInlineViewer({ content }: { content: string }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontSize,
      Indent,
      Link.configure({ openOnClick: false }),
      TiptapTable.configure({ resizable: false }),
      TiptapTableRow,
      TableHeaderExt,
      TiptapTableCell,
      ImageIconExtension.configure({ allowBase64: true }),
    ],
    editable: false,
    content: content,
    editorProps: {
      attributes: {
        style: 'outline:none; line-height:1.6;',
      },
    },
  });

  return <EditorContent editor={editor} />;
}

/**
 * 申請審査一覧テーブルコンポーネント
 * - 申請データの検索・ソート・ページング・カラム表示設定機能を提供する
 * - sessionStorageを使用して検索条件・表示設定を保持する
 */
const ApplicationList: React.FC = () => {
  const isMobile = useMediaQuery("(max-width:600px)"); // 600px 以下でモバイル表示
  const theme = useTheme();
  const [data, setData] = useState<ApplicationAuditType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => {
    return sessionStorage.getItem("searchText_ApplicationAudit") || ""; // sessionStorageに値がある場合はその値を格納。ない場合は空文字を格納。
  });
  // const [sortAsc, setSortAsc] = useState(true);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof ApplicationAuditType;
    asc: boolean;
  }>({
    key: "number",
    asc: false,
  });
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  // sessionStorageに値がある場合はその値を格納。ない場合はデフォルト値を格納。
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = sessionStorage.getItem("visibleColomns_ApplicationAudit");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set<string>(parsed);
        }
      } catch (e) {
        console.warn("visibleColumns の復元に失敗しました:", e);
      }
    }
    return new Set<string>(Object.keys(columnLabels));
  });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  // 表示件数のデフォルトと選択できる件数を設定。
  const defaultRowPerPage = 50;
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    return (
      Number(sessionStorage.getItem("showItems_ApplicationAudit")) ||
      defaultRowPerPage
    ); // sessionStorageに値がある場合はその値を格納。ない場合はデフォルト値を格納。
  });
  const rowPerPageList = [50, 100, 1000];
  const [page, setPage] = useState(() => {
    // sessionStorageに値がある場合はその値を格納。それ以外は1をセットする。
    const stored = sessionStorage.getItem("paging_ApplicationAudit");
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return !isNaN(parsed) && parsed > 0 ? parsed : 1;
  });
  const statusMap: Record<string, string> = {
    NotStarted: "申請前",
    InProgress: "申請中",
    Completed: "完了",
    Canceled: "取り下げ",
    Failed: "却下",
    Error: "エラー終了",
    SendBack: "差し戻し",
    Draft: "一時保存",
  };
  const navigate = useNavigate();

  // 初回レンダリング時に申請審査一覧データを取得する
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const result = await fetchApplicationAuditList();
      setData(result);
      setLoading(false);
    };
    loadData();
  }, []);

  // カラムの表示・非表示を切り替える
  const toggleColumn = (key: string) => {
    const newSet = new Set(visibleColumns);
    newSet.has(key) ? newSet.delete(key) : newSet.add(key);
    sessionStorage.setItem(
      "visibleColomns_ApplicationAudit",
      JSON.stringify(Array.from(newSet))
    ); // 表示するカラム情報をsessionStorageに格納する。
    setVisibleColumns(newSet);
  };

  // 行の展開・折りたたみを切り替える
  const toggleExpand = (id: string) => {
    const newSet = new Set(expandedRows);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setExpandedRows(newSet);
  };

  const filteredData = useMemo(() => {
    const keywords = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    let result = data.filter((row) =>
      keywords.every((keyword) =>
        Object.entries(row).some(([key, value]) => {
          let displayValue = "";

          // statusを日本語で検索できる
          if (key === "status" && typeof value === "string") {
            displayValue = statusMap[value] || "";
          } else if (typeof value === "string") {
            displayValue = value;
          } else if (key === "submittedAt") {
            displayValue = dayjs(value).isValid() ? dayjs(value).format("YYYY/MM/DD") : "未提出";
          }

          return displayValue.toLowerCase().includes(keyword);
        })
      )
    );

    if (sortConfig) {
      const { key, asc } = sortConfig;
      result = [...result].sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];

        if (aValue === undefined || bValue === undefined) return 0;

        // richTextMapはソート対象外
        if (key === "richTextMap") return 0;

        // 日付でソート
        if (key === "submittedAt") {
          return asc
            ? new Date(aValue as string | Date).getTime() - new Date(bValue as string | Date).getTime()
            : new Date(bValue as string | Date).getTime() - new Date(aValue as string | Date).getTime();
        }

        // numberをnumber型に変換してソート
        if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
          const aNum = Number(aValue);
          const bNum = Number(bValue);
          return asc ? aNum - bNum : bNum - aNum;
        }

        //デフォルト的にstringで比較する
        return asc
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    }

    setPage(1);
    return result;
  }, [searchTerm, data, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <CircularProgress />
      </div>
    );
  }

  // 遷移後画面
  const handleRowClick = (record: ApplicationAuditType) => {
    navigate(`/detail/${record.applicationEntryId}`, {
      state: { ...record, from: "ApplicationList" },
    });
  };

  // フィルターの条件を初期化する
  const filterReset = () => {
    // sessionStorageを削除する
    sessionStorage.removeItem("searchText_ApplicationAudit");
    sessionStorage.removeItem("showItems_ApplicationAudit");
    sessionStorage.removeItem("visibleColomns_ApplicationAudit");
    sessionStorage.removeItem("paging_ApplicationAudit");

    // フィルター条件の初期化
    setSearchTerm("");
    setVisibleColumns(new Set(Object.keys(columnLabels)));
    setRowsPerPage(defaultRowPerPage);
    setPage(1);
  };

  // テキストボックスのテキストをクリア
  const handleFilterTextClear = () => {
    // フィルター検索のテキストを初期化
    setSearchTerm("");
    // sessionStorageの値をクリアする
    sessionStorage.removeItem("searchText_ApplicationAudit");
  };

  return (
    <div className="p-4">
      <Box display="flex" alignItems="center" gap={2}>
        {/* 検索欄 */}
        <div className="mb-2 flex items-center gap-2">
          <TextField
            label="検索"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              // 空の場合はsessionStorageを削除。それ以外は値を格納する。
              if (value === "") {
                sessionStorage.removeItem("searchText_ApplicationAudit");
              } else {
                sessionStorage.setItem("searchText_ApplicationAudit", value);
              }
            }}
            className="w-64"
            sx={{ ml: 1, mt: 2, width: 220 }}
            slotProps={{
              input: {
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleFilterTextClear}
                      edge="end"
                      sx={{
                        backgroundColor: "transparent", // 背景透明
                        "&:hover": {
                          backgroundColor: "transparent", // ホバー時も透明
                        },
                      }}
                    >
                      <ClearSharpIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </div>
        {/* フィルターのリセット機能(初期化して、sessionStorageをclearにする) */}
        <Chip
          label="リセット"
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
            ml: 1,
            mt: 2,
          }}
          onClick={filterReset}
        />
      </Box>
      {/* 表示件数・表示項目設定 */}
      <div>
        <Button
          sx={{ ml: 1, mt: 1, typography: "buttonMedium" }}
          onClick={() => setShowColumnSettings(!showColumnSettings)}
          startIcon={showColumnSettings ? <ChevronUp /> : <ChevronDown />}
        >
          表示設定
        </Button>
      </div>
      <Box sx={{ pl: 2, pb: 1 }}>
        {showColumnSettings && (
          <div>
            <div>表示件数:</div>
            <RadioGroup
              row
              value={rowsPerPage.toString()}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                sessionStorage.setItem(
                  "showItems_ApplicationAudit",
                  e.target.value
                ); // 表示設定が変わったら、sessionStorageに値を格納する。
                setPage(1);
                sessionStorage.removeItem("paging_ApplicationAudit"); // 表示設定が変わったら、sessionStorageの値を削除する。
              }}
            >
              {rowPerPageList.map((num) => (
                <FormControlLabel
                  key={num}
                  value={num.toString()}
                  control={
                    <Radio
                      sx={{
                        color: "primary",
                        "&.Mui-checked": {},
                        transform: "scale(0.6)",
                      }}
                    />
                  }
                  label={`${num}件`}
                />
              ))}
            </RadioGroup>
            <div>
              <div className="font-semibold mb-2">表示項目:</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(columnLabels).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={visibleColumns.has(key)}
                      onChange={() => toggleColumn(key)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </Box>

      {/* ページング */}
      <Box
        sx={{
          ml: 2,
          my: 1,
          display: "flex",
          gap: 2,
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        {/* 件数表示 */}
        <div style={{ marginBottom: 1, color: "#666", fontSize: "1rem" }}>
          全 {filteredData.length} 件中{" "}
          {filteredData.length === 0
            ? "0 件 〜 0 件"
            : `${(page - 1) * rowsPerPage + 1} 件 〜 ${Math.min(
                page * rowsPerPage,
                filteredData.length
              )} 件`}{" "}
          を表示
        </div>
        <div>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => {
              setPage(value);
              sessionStorage.setItem(
                "paging_ApplicationAudit",
                value.toString()
              ); // ページが変わったら、現在のページをsessionStorageに格納する。
            }}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                minWidth: "20px",
                height: "20px",
                padding: "1px",
              },
            }}
          />
        </div>
      </Box>

      {/* テーブル */}
      <TableContainer
        component={Paper}
        sx={{
          ml: isMobile ? 0 : 1,
          mb: 2,
          overflow: "auto", // スクロールを表示
          maxHeight: "calc(100vh - 290px)",
          maxWidth: "100%",
        }}
      >
        <Table
          sx={{ tableLayout: "auto", minWidth: 800 }}
          aria-label="customized table"
          stickyHeader
        >
          <TableHead>
            <TableRow>
              {Object.entries(columnLabels).map(([key, label]) => {
                if (!visibleColumns.has(key)) return null;
                const isActive = sortConfig?.key === key;
                const asc = sortConfig?.asc;

                return (
                  <StyledTableCell
                    key={key}
                    onClick={() => {
                      setPage(1); // 第一ページに戻る
                      setSortConfig((prev) =>
                        prev?.key === key
                          ? {
                              key: key as keyof ApplicationAuditType,
                              asc: !prev.asc,
                            }
                          : {
                              key: key as keyof ApplicationAuditType,
                              asc: true,
                            }
                      );
                    }}
                    onMouseEnter={() => setHoveredColumn(key)}
                    onMouseLeave={() => setHoveredColumn(null)}
                    style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    <div
                      style={{ display: "inline-flex", alignItems: "center" }}
                    >
                      {label}
                      {(isActive || hoveredColumn === key) &&
                        (asc && isActive ? (
                          <ChevronUp
                            fontSize="small"
                            className="ml-1 text-gray-500"
                          />
                        ) : !asc && isActive ? (
                          <ChevronDown
                            fontSize="small"
                            className="ml-1 text-gray-500"
                          />
                        ) : (
                          <ChevronDown
                            fontSize="small"
                            className="ml-1 text-gray-300"
                          />
                        ))}
                    </div>
                  </StyledTableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <StyledTableRow
                key={row.applicationEntryId}
                onClick={(event) => {
                  event.stopPropagation(); // クリックエリアでイベントバブリング停止
                  handleRowClick(row);
                }}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#e3f2fd", // 浅いブルー
                    boxShadow: "inset 0 0 0 1px #90caf9",
                  },
                }}
              >
                {Object.entries(row)
                  .filter(
                    ([key]) => visibleColumns.has(key) && key in columnLabels
                  )
                  .map(([key, value]) => {
                    if (key === "formSummary") {
                      // richTextMapがある場合は、formSummaryを処理
                      let displayContent = value;
                      const richTextElements: React.ReactNode[] = [];

                      if (row.richTextMap) {
                        // formSummaryを行ごとに分割
                        const lines = value.split('\n');
                        lines.forEach((line: string, index: number) => {
                          // "label: [リッチテキスト]" の形式を検出
                          const match = line.match(/^(.+?):\s*\[リッチテキスト\]$/);
                          if (match) {
                            const label = match[1];
                            const htmlContent = row.richTextMap![label];
                            if (htmlContent) {
                              // labelを表示（太字なし）
                              richTextElements.push(
                                <div key={`label-${index}`}>
                                  {label}:
                                </div>
                              );
                              // Tiptapで読み取り専用表示
                              richTextElements.push(
                                <div key={`content-${index}`} style={{ marginLeft: '8px', marginBottom: '8px' }}>
                                  <TiptapInlineViewer content={htmlContent} />
                                </div>
                              );
                            }
                          } else {
                            // 通常の行
                            richTextElements.push(
                              <div key={`line-${index}`}>{line}</div>
                            );
                          }
                        });
                      }

                      return (
                        <StyledTableCell key={key}>
                          {row.richTextMap ? (
                            <Box
                              onClick={(e) => {
                                e.stopPropagation(); // イベントバブリングを停止
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation(); // リサイズ時のイベントバブリングを停止
                              }}
                              component="div"
                              sx={{
                                fontFamily: theme.typography.fontFamily,
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "2px",
                                minWidth: "250px",
                                height: "40px",
                                resize: "both",
                                overflow: "auto",
                                backgroundColor: "#f9f9f9",
                                width: "60%",
                                boxSizing: "border-box",
                                cursor: "default",
                                whiteSpace: "normal",
                                lineHeight: 1.6,
                                "& p": { margin: "0.25em 0" },
                                // テーブルスタイル
                                "& table": {
                                  borderCollapse: "collapse",
                                  width: "100%",
                                  margin: "0.5em 0",
                                },
                                "& th, & td": {
                                  border: "1px solid #dee2e6",
                                  padding: "0.5em",
                                  minWidth: "50px",
                                },
                                "& th": {
                                  backgroundColor: "#f8f9fa",
                                  fontWeight: "bold",
                                },
                                // 引用スタイル
                                "& blockquote": {
                                  borderLeft: "3px solid #ccc",
                                  paddingLeft: "1em",
                                  margin: "0.5em 0",
                                  color: "#666",
                                },
                                // コードブロックスタイル
                                "& pre": {
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: "4px",
                                  padding: "0.75em 1em",
                                  overflowX: "auto",
                                  fontFamily: "monospace",
                                },
                                "& code": {
                                  backgroundColor: "#f0f0f0",
                                  borderRadius: "3px",
                                  padding: "0.1em 0.3em",
                                  fontFamily: "monospace",
                                  fontSize: "0.9em",
                                },
                                // 水平線スタイル
                                "& hr": {
                                  border: "none",
                                  borderTop: "2px solid #dee2e6",
                                  margin: "1em 0",
                                },
                                // 画像スタイル
                                "& img": {
                                  maxWidth: "100%",
                                  height: "auto",
                                },
                              }}
                            >
                              {richTextElements}
                            </Box>
                          ) : (
                            <textarea
                              value={value}
                              readOnly
                              onClick={(e) => {
                                e.stopPropagation(); // イベントバブリングを停止
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation(); // リサイズ時のイベントバブリングを停止
                              }}
                              style={{
                                fontFamily: theme.typography.fontFamily,
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "2px",
                                minWidth: "250px",
                                height: "40px",
                                resize: "both",
                                overflow: "auto",
                                backgroundColor: "#f9f9f9",
                                width: "60%",
                                boxSizing: "border-box",
                                cursor: "default",
                              }}
                            />
                          )}
                        </StyledTableCell>
                      );
                    }

                    return (
                      <StyledTableCell key={key} align="left">
                        {key === "status" ? (statusMap[value] ?? value) : key === "submittedAt" ? (dayjs(value).isValid() ? dayjs(value).format("YYYY/MM/DD") : "未提出") : value}
                      </StyledTableCell>
                    );
                  })}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* ページング */}
      <Box
        sx={{
          ml: 2,
          display: "flex",
          gap: 2,
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        {/* 件数表示 */}
        <div style={{ marginTop: 1, color: "#666", fontSize: "1rem" }}>
          全 {filteredData.length} 件中{" "}
          {filteredData.length === 0
            ? "0 件 〜 0 件"
            : `${(page - 1) * rowsPerPage + 1} 件 〜 ${Math.min(
                page * rowsPerPage,
                filteredData.length
              )} 件`}{" "}
          を表示
        </div>
        <div>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                minWidth: "20px",
                height: "20px",
                padding: "1px",
              },
            }}
          />
        </div>
      </Box>
    </div>
  );
};

export default ApplicationList;
